import axios from "axios";
import getenvvar from "../utils/Env";
import { getAuthToken } from "assets/global";

export const baseURL = getenvvar("REACT_APP_API_BASE_URL");

/**
 * Axios main instance creation.
 */
const app = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*"
  }
});

/**
 * Method to log out, clears the localStorage and reloads
 * the browser location.
 *
 * @param { object } [options]
 * @param { boolean } [options.tokenExpired] - Leave the 'TokenExpired' flag in
 *   sessionStorage so the login page shows the inactivity warning. It has to
 *   be written here, after sessionStorage is cleared: setting it before
 *   calling logout() got it wiped straight away and the warning never showed.
 */
const logout = ({ tokenExpired = false } = {}) => {
  const user_locale = localStorage.getItem("user_locale");
  const cookieConsent = localStorage.getItem("CookieConsent");

  localStorage.clear();
  localStorage.setItem("user_locale", user_locale);
  localStorage.setItem("CookieConsent", cookieConsent);
  sessionStorage.clear();
  if (tokenExpired) {
    sessionStorage.setItem("TokenExpired", true);
  }
  window.location.reload();
};

/**
 * Token that was actually sent with a failed request, read from its
 * Authorization header.
 *
 * @param { object } response - axios response of the failed request
 * @returns { string|null }
 */
const getSentToken = response => {
  const headers = response?.config?.headers;
  const value =
    (typeof headers?.get === "function" && headers.get("Authorization")) ||
    headers?.Authorization ||
    headers?.authorization;
  return typeof value === "string" ? value.replace(/^Token\s+/, "") : null;
};

// Single in-flight check per token so a burst of 401s only probes the
// backend once. Keyed by token: a check for a token that has since been
// replaced must not be reused for its replacement.
let sessionCheck = null; // { token, promise }

/**
 * Asks the backend whether the given session token is still accepted.
 * Only a 401 on this probe means the token is dead; any other failure
 * (network, 5xx) is no evidence and keeps the session. A bare axios call is
 * used so the probe never re-enters the interceptors of `app`.
 *
 * @param { string } token - The exact token to validate.
 * @returns { Promise<boolean> }
 */
const isSessionTokenValid = token => {
  if (!sessionCheck || sessionCheck.token !== token) {
    const promise = axios
      .get(`${baseURL}/users/user-profile/`, {
        headers: { Authorization: `Token ${token}` }
      })
      .then(() => true)
      .catch(error => error?.response?.status !== 401)
      .finally(() => {
        if (sessionCheck?.token === token) {
          sessionCheck = null;
        }
      });
    sessionCheck = { token, promise };
  }
  return sessionCheck.promise;
};

/**
 * Method to return server message errors. Otherwise,
 * you'll just get generic status errors.
 *
 * This method is exported and implemented by
 * app_reload_scenario_callback and app_limited_concurrency.
 *
 * @param { object } err
 * @returns
 */
export function showBackendError(err) {
  try {
    // A 401 can mean the stored token is no longer usable (invalid, wrong
    // type, expired, inactive user...) or a denied action inside a valid
    // session (e.g. editing another user's catalogued scenario), and the
    // message is localized, so neither the status nor the text tells them
    // apart. The backend is asked instead: the session is only cleared when
    // a probe with the stored token is itself rejected. Keeping a dead token
    // in storage makes the login page reload against it indefinitely.
    //
    // Only a 401 to a request that carried the currently stored token is
    // considered. Requests made while logged out (the login page issues
    // some, e.g. capabilities) or with a token that has since been replaced
    // say nothing about the current session.
    if (err.status === 401) {
      const storedToken = getAuthToken();

      if (storedToken && getSentToken(err) === storedToken) {
        const detail =
          typeof err.data?.detail === "string" ? err.data.detail : "";

        // An inactivity expiry gets a warning on the login page. logout()
        // writes the flag itself, after clearing storage.
        const tokenExpired =
          detail.includes("token has expired") ||
          detail.includes("inactividad") ||
          detail.includes("Inaktivität") ||
          detail.includes("inactivité");

        isSessionTokenValid(storedToken).then(valid => {
          // The token may have been replaced while the probe was pending
          // (a new login, another tab with remember-me). Only the session
          // that was actually checked is cleared.
          if (!valid && getAuthToken() === storedToken) {
            logout({ tokenExpired });
          }
        });
      }
    }
    if (err.status === 500) {
      let msg = "Internal server error";
      return msg;
    } else if (err.status === 400) {
      let msg = "";
      Object.entries(err.data).map((item, index) => {
        if (index > 0) {
          msg = msg.concat(" ,");
        }
        msg = msg.concat(item.toString().replace(",", ": "));
        return msg;
      });
      return msg;
    } else {
      let msg = "";
      Object.values(err.data).map(item => (msg = msg.concat(item + " ")));
      return msg;
    }
  } catch (e) {
    return "Server error";
  }
}

/**
 * Axios REQUEST Interceptor.
 */
app.interceptors.request.use(
  config => {
    // In every request, we get the auth token and
    // update the header
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Token ${getAuthToken()}`;

    // We also inject the header for the current language.
    config.headers["Accept-Language"] = localStorage.getItem("user_locale");
    return config;
  },
  error => {
    // We parse the errors with showBackendErrors.
    return Promise.reject(showBackendError(error.response));
  }
);

/**
 * Axios RESPONSE Interceptor.
 */
app.interceptors.response.use(
  response => response,
  // We parse the errors with showBackendErrors.
  error => {
    return Promise.reject(showBackendError(error.response));
  }
);

export default app;
